import { Fragment, isValidElement, type ReactNode } from "react";
import { LogOut } from "lucide-react";
import { LogoLogin } from "../../../components/LoginLogo";
import { ICON_STROKE } from "../../../components/icons/icon-tokens";

// Premium "display" typography for the auth hero. Techniques borrowed from
// Apple, Stripe, Linear, Vercel marketing pages — the high-end look comes
// less from a special font than from the small details below:
//
//   • Large display size — 32 → 64px depending on viewport
//   • Tight letter-spacing (-0.03em) — pulls letters close on big headlines
//     so the silhouette reads as one shape, not as separate glyphs
//   • Tight leading (1.02-1.05) — display text needs less air than body
//   • font-weight 600 (semibold) on Geist — premium sites lean on 500-600
//     more often than 700; reads "confident" rather than "shouty"
//   • text-wrap: balance — distributes words across lines evenly so the
//     paragraph forms a clean rectangle instead of orphaned tails
//   • text-wrap: pretty — prevents single-word last lines on body copy
//   • font-feature-settings — turns on optical kerning + contextual
//     alternates so the type renders the way the foundry intended
//   • Warm-dark ink (#1C1B19) instead of pure black — softer on cream/white
//
// On mobile the wordmark + copy stack centered (the auth Layout no longer
// renders a fixed top-bar logo). On desktop the block is left-aligned to sit
// flush against the form column on the right.

/**
 * Some onboarding callers pass their description text wrapped in their own
 * `<p className="text-content-primary">` element, which would otherwise win
 * the cascade and leave the body copy unreadable on the dark photo wash.
 * Walk the tree and pull out the raw text/nodes so we can re-render the
 * description inside our own controlled `<p>`.
 */
function extractDescription(node: ReactNode): ReactNode {
  if (node === null || node === undefined || typeof node === "boolean") {
    return null;
  }
  if (typeof node === "string" || typeof node === "number") {
    return node;
  }
  if (Array.isArray(node)) {
    return node.map((n, i) => (
      <Fragment key={i}>{extractDescription(n)}</Fragment>
    ));
  }
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return extractDescription(props.children);
  }
  return null;
}

type AutumnInfoVariant = "default" | "marketing";

// Single source of truth for the auth-hero copy. `default` is the standard
// pitch (with the hand-placed line breaks that give the headline its 3-line
// silhouette); `marketing` is the campaign copy shown on marketing signups
// (/register-marketing or ?type=marketing). Onboarding callers bypass this
// entirely by passing explicit `title`/`description` props.
const COPY: Record<AutumnInfoVariant, { title: ReactNode; description: string }> = {
  default: {
    title: (
      <>
        A new, intelligent
        <br />
        operating system
        <br />
        for independent hotels.
      </>
    ),
    description:
      "Autumn is an AI-native hotel revenue platform founded by industry veterans and leading AI engineers, backed by the best investors, and trusted by hundreds of hotels globally.",
  },
  marketing: {
    title: "Autumn: digital marketing for one of a kind gems.",
    description:
      "Best-in-class hospitality marketing, supercharged by the newest technology, purpose-built for unique independent properties.",
  },
};

interface AutumnInfoProps {
  /**
   * Inverts the colour palette to white for use over the dark hero photo
   * gradient on /login, /register, and /onboarding.
   */
  invert?: boolean;
  /**
   * Selects which `COPY` entry seeds the headline/body when `title`/
   * `description` aren't explicitly overridden. `"marketing"` shows the
   * campaign copy on marketing signups; defaults to `"default"`.
   */
  variant?: AutumnInfoVariant;
  /**
   * Optional override for the headline. When omitted, the `variant` copy
   * renders. Onboarding screens pass the step title here ("Connect Mews",
   * "Hotel Info", …).
   */
  title?: ReactNode;
  /**
   * Optional override for the body copy. Defaults to the autumn elevator
   * pitch. Onboarding screens pass the step description here.
   */
  description?: ReactNode;
  /**
   * Promote the description to a larger display size (24px on desktop,
   * scaled down on smaller viewports). Used by onboarding so the step's
   * one-line context reads as a confident sub-headline rather than as
   * fine print. CSS transition makes the size change between the auth
   * default (15-16px) and the onboarding "large" smoothly animated, so
   * /register → /onboarding doesn't pop.
   */
  descriptionSize?: "default" | "large";
  /**
   * Render the bottom-centre Logout pill. Only used by onboarding; the
   * standalone auth pages never want it.
   */
  showLogout?: boolean;
  /** Click handler for the Logout pill — required when `showLogout`. */
  onLogout?: () => void;
  /**
   * Optional element rendered just above the Logout button. Used by onboarding
   * to surface the "Exit setup" (continue-later / discard) affordance for an
   * added property. Auth pages never pass it, so their layout is unchanged.
   */
  secondaryAction?: ReactNode;
  /**
   * Optional element rendered at the top of the bottom-left footer stack (above
   * `secondaryAction` and Logout). Used by the onboarding final screen to show
   * the property switcher + "Add property" controls. Auth pages never pass it.
   */
  footerSlot?: ReactNode;
}

/**
 * Hero block used in the left column of the auth + onboarding shell. The
 * SAME React component is rendered in both contexts — only the props
 * change between /login (default copy) and /onboarding (step copy +
 * larger description + logout button). Because the component instance
 * doesn't change across the transition, React reconciles in place and
 * the CSS `font-size`/`color` transitions tween smoothly instead of
 * popping at remount.
 */
export function AutumnInfo({
  invert = false,
  variant = "default",
  title,
  description,
  descriptionSize = "default",
  showLogout = false,
  onLogout,
  secondaryAction,
  footerSlot,
}: AutumnInfoProps) {
  // Explicit `title`/`description` props (onboarding) win; otherwise the
  // hero copy comes from the selected `variant`.
  const resolvedTitle = title ?? COPY[variant].title;
  const resolvedDescription = description ?? COPY[variant].description;
  const headingColor = invert ? "#FFFFFF" : "var(--d-ink)";
  const bodyColor = invert
    ? "rgba(255, 255, 255, 0.92)"
    : "var(--d-ink-muted)";

  const isLarge = descriptionSize === "large";

  return (
    <div className="relative w-full h-full flex items-center justify-start dlg:justify-center">
      {/* `w-fit` makes the block as wide as its widest line, `mx-auto`
          centres it horizontally, `max-w-full` keeps it from overflowing
          on narrow viewports. Internal alignment stays left so logo +
          headline + body copy line up on the same left edge. */}
      <div className="w-fit max-w-full mx-0 dlg:mx-auto flex flex-col items-start text-left">
        {/* < 576px viewports: shrink the wordmark to 110px wide so it
            doesn't dominate the cramped mobile hero. `[&_svg]:` targets
            the SVG inside LogoLogin's wrapper; `h-auto` lets the
            viewBox keep its aspect ratio while width clamps. */}
        <LogoLogin
          invert={invert}
          className="[@media(max-width:575px)]:[&_svg]:w-[110px] [@media(max-width:575px)]:[&_svg]:h-auto"
        />
        <div className="mt-8 dlg:mt-12 flex flex-col gap-5 dlg:gap-6">
          <h1
            className="[@media(max-width:575px)]:text-[24px] text-[32px] sm:text-[40px] md:text-[44px] dlg:text-[44px] xl:text-[52px] font-semibold m-0"
            style={{
              fontFamily: "var(--d-font-sans)",
              color: headingColor,
              letterSpacing: "-0.03em",
              lineHeight: 1.04,
              fontFeatureSettings:
                "'kern' 1, 'liga' 1, 'calt' 1, 'ss01' 1, 'cv01' 1",
              WebkitFontSmoothing: "antialiased",
              MozOsxFontSmoothing: "grayscale",
              textRendering: "optimizeLegibility",
              // Smooth tween between /login / /register and /onboarding
              // since the same node animates in place rather than remounting.
              transition:
                "color 240ms var(--d-ease, ease), letter-spacing 240ms var(--d-ease, ease)",
            }}
          >
            {resolvedTitle}
          </h1>
          <p
            className={`max-w-[520px] m-0 [@media(max-width:575px)]:text-[12px] ${
              isLarge
                ? "text-[18px] sm:text-[20px] dlg:text-[24px]"
                : "text-[15px] sm:text-[16px] dlg:text-[16px]"
            }`}
            style={{
              fontFamily: "var(--d-font-body)",
              color: bodyColor,
              lineHeight: isLarge ? 1.45 : 1.6,
              letterSpacing: "-0.005em",
              textWrap: "pretty",
              fontFeatureSettings: "'kern' 1, 'liga' 1, 'calt' 1",
              // Same trick as above — when descriptionSize toggles between
              // default ↔ large the type tweens up/down instead of jumping.
              transition:
                "font-size 320ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1)), line-height 320ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1)), color 240ms var(--d-ease, ease)",
            }}
          >
            {extractDescription(resolvedDescription)}
          </p>
        </div>

        {/* Onboarding footer pinned to the bottom of the column: optional
            property controls (switcher + add property), the "Exit setup"
            action, and the Logout button. Only onboarding sets `showLogout`;
            auth pages render nothing here, so their layout is unchanged.

            Layout differs by breakpoint:
              • Desktop (dlg+): vertical LEFT-ALIGNED stack pinned to the
                bottom-left — switcher on top, Logout at the bottom (the tall
                column has room to spare).
              • Mobile/stacked (< dlg), no footerSlot (every step but the
                final one): the stack is just Logout (± "Exit setup") — pin
                it COMPACT to the TOP-RIGHT, level with the wordmark, clear
                of the title/copy on the left.
              • Mobile/stacked (< dlg), WITH footerSlot (final/Congrats
                screen): the property switcher + "Add property" stack is
                ~3 rows tall — overlaid top-right it collided with the
                "Thank you very much!" title, and in-flow it bloated the
                hero band. These are non-critical actions, so hide the
                whole stack here; Congrats re-renders it at the BOTTOM of
                its scrollable card instead (see Congrats.tsx). */}
        {showLogout && (
          <div
            className={`flex-col gap-2 dlg:flex dlg:absolute dlg:top-auto dlg:bottom-0 dlg:left-0 dlg:right-auto dlg:items-start dlg:gap-3 ${
              footerSlot
                ? "hidden"
                : "flex absolute top-0 right-0 items-end"
            } ${invert ? "text-white" : "text-content-secondary"}`}
          >
            {footerSlot}
            {secondaryAction}
            <button
              type="button"
              onClick={onLogout}
              className="inline-flex shrink-0 gap-1.5 items-center opacity-70 hover:opacity-100"
              style={{
                transition:
                  "opacity 380ms var(--d-ease, cubic-bezier(0.4, 0, 0.2, 1))",
              }}
            >
              <LogOut size={16} strokeWidth={ICON_STROKE} />
              <span className="text-sm tracking-[-0.4px]">Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
